import { css } from "@linaria/core";
import cx from "classnames";
import React, { type ReactElement, type SVGProps } from "react";
import { color, easingSoft, fonts, lineHeights, radius } from "..";
import { spacings } from "../Theme";
import TooltipV2 from "../TooltipV2";
import OverlayWrapper from "../TooltipV2/OverlayWrapper";

const SELECTED_CN = "selected";
const DISABLED_CN = "disabled";
const NO_SELECTION_CN = "no-selection";
const PADDING_PX = 4;

const transitionMs = 250;

export interface Option<T extends string> {
  label: string | React.ReactNode;
  title?: string;
  value: T;
  disabled?: boolean;
  hidden?: boolean;
  tooltipText?: string;
  icon?: ReactElement<SVGProps<SVGSVGElement>>;
}
interface SelectorProps<T extends string> {
  options: readonly Option<T>[];
  onChange: (value: T) => void;
  value: T | null;
  disabled?: boolean;
  className?: string;
}

const segmentedOptions = css`
  width: fit-content;
  flex: none;
  position: relative;
  display: flex;
  column-gap: ${spacings.xxs};
  padding: ${spacings.xxs};
  border-radius: ${radius};
  font: ${fonts.body2SemiBold};
  line-height: ${lineHeights[0]};
  &.large {
    font: ${fonts.body1};
  }
  color: ${color.carbon800};
  background-color: ${color.titanium100};
  overflow: hidden;
`;

const segmentedOptionMarker = css`
  z-index: 1;
  position: absolute;
  bottom: ${spacings.xxs};
  top: ${spacings.xxs};
  border-radius: ${radius};
  background-color: ${color.hydrogen};

  will-change: transform, width, opacity;
  &.${NO_SELECTION_CN} {
    transition: opacity ${transitionMs}ms ${easingSoft};
  }
  :not(.${NO_SELECTION_CN}) {
    transition: transform ${transitionMs}ms ${easingSoft};
  }
`;

const segmentedOptionItem = css`
  z-index: 2;
  padding: ${spacings.xxs} ${spacings.xs};
  border-radius: ${radius};
  transition: background-color ${transitionMs}ms ${easingSoft};
  :hover:not(.${SELECTED_CN}):not(.${DISABLED_CN}) {
    background-color: ${color.titanium50};
  }

  &.${DISABLED_CN} {
    opacity: 0.5;
    cursor: not-allowed;
  }

  cursor: pointer;
  white-space: nowrap;

  &:has(svg) {
    display: flex;
    align-items: center;
    padding: ${spacings.xxs};
    svg {
      margin-right: ${spacings.xxs};
    }
  }
`;

const segmentedOptionTooltip = css`
  max-width: 350px;
`;

const Selector = <T extends string>({
  options,
  onChange,
  value,
  disabled,
  className,
}: SelectorProps<T>) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const markerRef = React.useRef<HTMLDivElement>(null);
  const enabledOptions = React.useMemo(
    () => options.filter(option => !(option.disabled || option.hidden)),
    [options],
  );

  const repaint = React.useCallback((value: T | null) => {
    const wrapper = wrapperRef.current;
    const marker = markerRef.current;
    if (!wrapper || !marker) throw new Error("missing ref");

    if (value === null) {
      window.requestAnimationFrame(() => {
        marker.classList.add(NO_SELECTION_CN);
        marker.style.opacity = "0%";
      });
      return;
    }

    const selectedElement = wrapper.querySelector(`[data-value="${value}"]`);

    if (selectedElement === null) throw new Error(`Cannot find [data-value="${value}"]`);

    const wrapperRect = wrapper.getBoundingClientRect();
    const selectedRect = selectedElement.getBoundingClientRect();

    if (!selectedRect || !wrapperRect) throw new Error("Missing rects on elements");

    const left = selectedRect.x - wrapperRect.x - PADDING_PX;

    /**
     * Improve animation performance using transform instead of left/right
     * to promote animation into a new stacking context,
     * enabling better browser optimization.
     *
     * Better handling of transition end, to remove class using transitionend event.
     */
    marker.addEventListener("transitionend", () => marker.classList.remove(NO_SELECTION_CN), {
      once: true,
    });

    window.requestAnimationFrame(() => {
      marker.style.transform = `translateX(${left}px)`;
      marker.style.width = `${selectedRect.width}px`;
      marker.style.opacity = "100%";
    });
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (["ArrowRight", "ArrowLeft"].includes(e.key)) {
        e.preventDefault();
        const currentIndex = value
          ? enabledOptions.findIndex(option => option.value === value)
          : e.key === "ArrowLeft"
            ? enabledOptions.length
            : -1;
        const option = enabledOptions[e.key === "ArrowLeft" ? currentIndex - 1 : currentIndex + 1];
        if (option) onChange(option.value);
      }
    },
    [onChange, enabledOptions, value],
  );

  React.useLayoutEffect(() => {
    repaint(value);
  }, [repaint, value]);

  return (
    <div
      className={cx(segmentedOptions, className)}
      tabIndex={0}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
    >
      {options.map(option =>
        option.hidden ? null : (
          <TooltipV2
            key={option.value}
            placement="bottom"
            overlay={<OverlayWrapper text={option.tooltipText} />}
            style={{ visibility: option.tooltipText ? "visible" : "hidden" }}
            className={segmentedOptionTooltip}
          >
            <div
              onClick={() => !option.disabled && onChange(option.value)}
              data-value={option.value}
              className={cx(segmentedOptionItem, {
                [SELECTED_CN]: option.value === value,
                [DISABLED_CN]: Boolean(option.disabled || disabled),
              })}
              title={option.title}
            >
              {option.icon}
              {option.label}
            </div>
          </TooltipV2>
        ),
      )}
      <div ref={markerRef} className={segmentedOptionMarker} />
    </div>
  );
};

export default React.memo(Selector) as typeof Selector;
